import React, { useState } from 'react';
import {
  Box,
  Chip,
  Popover,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../theme/colors';

interface FactsPanelProps {
  facts: Record<string, unknown> | null | undefined;
}

/**
 * Displays current plan facts (user decisions) as a chip that expands to show details.
 * Facts are key-value pairs like { delivery_method: "certified_mail" }
 */
export const FactsPanel: React.FC<FactsPanelProps> = ({ facts }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Parse facts
  const factEntries = facts && typeof facts === 'object'
    ? Object.entries(facts).filter(([_, v]) => v !== null && v !== undefined)
    : [];

  const factCount = factEntries.length;

  // Format fact key for display (snake_case to Title Case)
  const formatKey = (key: string): string => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format fact value for display
  const formatValue = (value: unknown): string => {
    if (typeof value === 'string') {
      return value
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return String(value);
  };

  if (factCount === 0) {
    return (
      <Tooltip title="No decisions recorded yet">
        <Chip
          icon={<FactCheckIcon sx={{ fontSize: 16 }} />}
          label="No facts"
          size="small"
          variant="outlined"
          sx={{
            bgcolor: 'transparent',
            borderColor: colors.gray[300],
            color: colors.textSecondary,
            fontSize: '0.75rem',
            height: 26,
            '& .MuiChip-icon': {
              color: colors.gray[400],
            },
          }}
        />
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="View recorded decisions">
        <Chip
          icon={<FactCheckIcon sx={{ fontSize: 16 }} />}
          label={`${factCount} fact${factCount !== 1 ? 's' : ''}`}
          size="small"
          onClick={handleClick}
          sx={{
            bgcolor: `${colors.blue[600]}10`,
            borderColor: colors.blue[300],
            color: colors.blue[700],
            fontSize: '0.75rem',
            height: 26,
            cursor: 'pointer',
            '&:hover': {
              bgcolor: `${colors.blue[600]}20`,
            },
            '& .MuiChip-icon': {
              color: colors.blue[600],
            },
          }}
        />
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: 280,
            maxWidth: 400,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FactCheckIcon sx={{ fontSize: 20, color: colors.blue[600] }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                Plan Decisions
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose} sx={{ color: colors.textSecondary }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block', mb: 2 }}>
            These decisions affect which steps are applicable to your plan.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Facts list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {factEntries.map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 1.5,
                  bgcolor: colors.gray[50],
                  borderRadius: 1,
                  border: `1px solid ${colors.gray[200]}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: colors.textSecondary, fontWeight: 500 }}
                >
                  {formatKey(key)}
                </Typography>
                <Chip
                  label={formatValue(value)}
                  size="small"
                  sx={{
                    bgcolor: colors.blue[50],
                    color: colors.blue[700],
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
            ))}
          </Box>

          {/* Footer hint */}
          <Typography
            variant="caption"
            sx={{
              color: colors.textSecondary,
              display: 'block',
              mt: 2,
              fontStyle: 'italic',
            }}
          >
            Facts are extracted from your chat messages when you make decisions.
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default FactsPanel;
